import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCompletePayment, paymentRequestButtonShow } from './events.js';

var _excluded = ["stripeKey"];

// Button is the default case. Set to card when showing the form

var SUBCURRENCY_MULTIPLIER = 100;
var StripeContext = React.createContext({});
var StripePaymentProviderInner = function StripePaymentProviderInner(_ref) {
  var client = _ref.client,
    amountToPay = _ref.amountToPay,
    quoteCurrency = _ref.quoteCurrency,
    storeInfo = _ref.storeInfo,
    confirmStripePaymentIntentQuery = _ref.confirmStripePaymentIntentQuery,
    createStripePaymentIntentQuery = _ref.createStripePaymentIntentQuery,
    children = _ref.children;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    browser = _useState2[0],
    setBrowser = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    stripePaymentReq = _useState4[0],
    setStripePaymentReq = _useState4[1];
  var stripe = useStripe();
  var _useState5 = useState("DEFAULT"),
    _useState6 = _slicedToArray(_useState5, 2),
    paymentMode = _useState6[0],
    setPaymentMode = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    canMakeButtonPayment = _useState8[0],
    setCanMakeButtonPayment = _useState8[1];
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    billingAddress = _useState10[0],
    setBillingAddress = _useState10[1];
  var _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    email = _useState12[0],
    setEmail = _useState12[1];
  var completePayment = useCompletePayment(email, billingAddress);
  var payWithStripe = function payWithStripe() {
    if (!stripePaymentReq) {
      throw new Error("Stripe not loaded");
    }

    // Card payment
    if (paymentMode === "CARD") {
      return completePayment({
        stripe: stripe,
        client: client,
        paymentMode: paymentMode,
        confirmStripePaymentIntentQuery: confirmStripePaymentIntentQuery,
        createStripePaymentIntentQuery: createStripePaymentIntentQuery
      });
    }

    // Button payment
    if (paymentMode === "DEFAULT") {
      return paymentRequestButtonShow(stripePaymentReq).then(function (ev) {
        return completePayment({
          stripe: stripe,
          client: client,
          paymentMode: paymentMode,
          paymentMethodID: ev.paymentMethod.id,
          confirmStripePaymentIntentQuery: confirmStripePaymentIntentQuery,
          createStripePaymentIntentQuery: createStripePaymentIntentQuery
        });
      });
    }
    throw new Error("Invalid stripeMethod");
  };

  // Give Stripe the data when amountToPay is updated
  useEffect(function () {
    if (stripe) {
      var shopName = storeInfo.name;
      var country = storeInfo.defaultCountry.code;
      /*
      * use quote currency if it's set, otherwise use store currency
      * issues when using storeInfo in
      * "nested stores" e.g. SAS buy & transfer
      * storeInfo on "wrapping" store differs
      * from quote baseCurrrency on "nested" store
      */
      var currency = quoteCurrency ? quoteCurrency.toLowerCase() : storeInfo.baseCurrencyCode.toLowerCase();
      setStripePaymentReq(stripe.paymentRequest({
        country: country,
        currency: currency,
        total: {
          label: shopName,
          amount: Math.floor(amountToPay * SUBCURRENCY_MULTIPLIER)
        }
      }));
    }
  }, [stripe, amountToPay, storeInfo]);

  // Set can make button payment
  useEffect(function () {
    if (stripePaymentReq) {
      stripePaymentReq.canMakePayment().then(function (result) {
        if (result) {
          setBrowser(result.applePay ? "apple" : "other");
          setCanMakeButtonPayment(Boolean(result));
        }
      });
    }
  }, [stripePaymentReq, stripe, setBrowser]);
  return /*#__PURE__*/React.createElement(StripeContext.Provider, {
    value: {
      confirmStripePaymentIntentQuery: confirmStripePaymentIntentQuery,
      createStripePaymentIntentQuery: createStripePaymentIntentQuery,
      paymentMode: paymentMode,
      setPaymentMode: setPaymentMode,
      stripePaymentReq: stripePaymentReq,
      setStripePaymentReq: setStripePaymentReq,
      browser: browser,
      setBrowser: setBrowser,
      payWithStripe: payWithStripe,
      stripe: stripe,
      storeInfo: storeInfo,
      amountToPay: amountToPay,
      canMakeButtonPayment: canMakeButtonPayment,
      setCanMakeButtonPayment: setCanMakeButtonPayment,
      billingAddress: billingAddress,
      setBillingAddress: setBillingAddress,
      email: email,
      setEmail: setEmail
    }
  }, children);
};
var StripePaymentProvider = function StripePaymentProvider(_ref2) {
  var stripeKey = _ref2.stripeKey,
    props = _objectWithoutProperties(_ref2, _excluded);
  var _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    stripePromise = _useState14[0],
    setStripePromise = _useState14[1];

  /*
   * Load stripe script here instead of StripePaymentProviderInner
   * beacuse it's needed in <Elements />. The rest of the effects
   * is placed in  StripePaymentProviderInner
  */
  useEffect(function () {
    if (!stripePromise && stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    }
  }, [stripeKey]);
  return /*#__PURE__*/React.createElement(Elements, {
    stripe: stripePromise
  }, /*#__PURE__*/React.createElement(StripePaymentProviderInner, props));
};

export { SUBCURRENCY_MULTIPLIER, StripeContext, StripePaymentProvider };
//# sourceMappingURL=context.js.map
